import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft, Calendar, BedDouble, User, Mail, MapPin, Hash, CreditCard, CheckCircle2, XCircle, Loader2, LogOut, Star, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { bookingsApi, reviewsApi, type BookingListResponse } from "@/lib/api";
import { formatNaira } from "@/data/venues";
import { useAuth } from "@/context/AuthContext";

export const Route = createFileRoute("/admin/bookings/$id")({ component: BookingDetailPage });

const statusColor: Record<string, string> = {
  manager_approval_pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-blue-100 text-blue-800",
  checked_in: "bg-green-100 text-green-800",
  checked_out: "bg-gray-100 text-gray-800",
  cancelled: "bg-red-100 text-red-800",
};

function BookingDetailPage() {
  const { id } = Route.useParams();
  const router = useRouter();
  const { isVenueManager, isAdmin } = useAuth();
  const [booking, setBooking] = useState<BookingListResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBooking(); }, [id]);

  async function fetchBooking() {
    setLoading(true);
    try { const res = await bookingsApi.getById(id); setBooking(res); }
    catch (e: any) { toast.error(e?.message || "Failed to load booking"); }
    finally { setLoading(false); }
  }

  const [processing, setProcessing] = useState<string | null>(null);
  const [showReview, setShowReview] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  async function handleAction(action: string) {
    setProcessing(action);
    try {
      if (action === "approve") await bookingsApi.approve(id);
      else if (action === "reject") await bookingsApi.reject(id, "Manager rejected");
      else if (action === "checkout") await bookingsApi.checkout(id);
      toast.success(action + " completed");
      fetchBooking();
    } catch (e: any) { toast.error(e?.message || action + " failed"); }
    finally { setProcessing(null); }
  }

  async function submitReview() {
    if (!booking) return;
    try { await reviewsApi.create(booking.venueId as string, { rating, comment, bookingId: id }); toast.success("Review submitted"); setShowReview(false); }
    catch (e: any) { toast.error(e?.message || "Review failed"); }
  }

  const canManage = isVenueManager || isAdmin;

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  if (!booking) return <div className="p-8 text-center text-muted-foreground">Booking not found.<Button variant="outline" className="mt-4" onClick={() => router.history.back()}><ArrowLeft className="mr-2 h-4 w-4" /> Go Back</Button></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <Button variant="ghost" onClick={() => router.history.back()}><ArrowLeft className="mr-2 h-4 w-4" /> Back to Bookings</Button>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Booking {booking.bookingRef}</h1>
          <p className="text-muted-foreground flex items-center gap-2 mt-1"><Calendar className="h-4 w-4" /> {new Date(booking.startDate).toLocaleDateString()} — {new Date(booking.endDate).toLocaleDateString()}</p>
        </div>
        <Badge className={statusColor[booking.status] || "bg-gray-100"}>{booking.status.replace(/_/g, " ")}</Badge>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <section className="border rounded-lg p-5 bg-card">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><BedDouble className="h-5 w-5 text-primary" /> Venue &amp; Room</h2>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" /> {booking.venueName}</p>
              <p className="flex items-center gap-2"><BedDouble className="h-4 w-4 text-muted-foreground" /> {booking.roomTypeName || "Standard"}</p>
              <p className="flex items-center gap-2"><Hash className="h-4 w-4 text-muted-foreground" /> {booking.daysCount} night(s)</p>
            </div>
          </section>
          <section className="border rounded-lg p-5 bg-card">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><User className="h-5 w-5 text-primary" /> Guest Details</h2>
            <div className="space-y-2 text-sm">
              <p className="flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> {booking.guestName}</p>
              {booking.guestEmail && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" /> {booking.guestEmail}</p>}
            </div>
          </section>
          {canManage && (
            <section className="border rounded-lg p-5 bg-card">
              <h2 className="font-semibold mb-4">Manager Actions</h2>
              <div className="flex flex-wrap gap-3">
                {booking.status === "manager_approval_pending" && (
                  <>
                    <Button onClick={() => handleAction("approve")} disabled={!!processing}>{processing === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Approve</Button>
                    <Button variant="destructive" onClick={() => handleAction("reject")} disabled={!!processing}>{processing === "reject" ? <Loader2 className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />} Reject</Button>
                  </>
                )}
                {booking.status === "checked_in" && (
                  <Button onClick={() => handleAction("checkout")} disabled={!!processing}>{processing === "checkout" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4 mr-2" />} Check Out</Button>
                )}
              </div>
            </section>
          )}
          {booking.status === "checked_out" && !showReview && (
            <section className="border rounded-lg p-5 bg-card">
              <h2 className="font-semibold mb-4 flex items-center gap-2"><Star className="h-5 w-5 text-primary" /> Leave a Review</h2>
              <Button variant="outline" onClick={() => setShowReview(true)}><MessageSquare className="h-4 w-4 mr-2" /> Write Review</Button>
            </section>
          )}
          {showReview && (
            <section className="border rounded-lg p-5 bg-card">
              <h2 className="font-semibold mb-4 flex items-center gap-2"><Star className="h-5 w-5 text-primary" /> Leave a Review</h2>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <label htmlFor="rating" className="text-sm">Rating:</label>
                  <select id="rating" value={rating} onChange={(e) => setRating(Number(e.target.value))} className="border rounded px-2 py-1">
                    {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Star{n>1?"s":""}</option>)}
                  </select>
                </div>
                <textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience..." className="w-full border rounded-lg p-3 min-h-[100px]" />
                <div className="flex gap-2">
                  <Button onClick={submitReview}>Submit Review</Button>
                  <Button variant="ghost" onClick={() => setShowReview(false)}>Cancel</Button>
                </div>
              </div>
            </section>
          )}
        </div>
        <div className="space-y-6">
          <section className="border rounded-lg p-5 bg-card">
            <h2 className="font-semibold mb-4 flex items-center gap-2"><CreditCard className="h-5 w-5 text-primary" /> Payment</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-medium">{formatNaira(booking.totalAmount)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Per night</span><span>{formatNaira(booking.pricePerNight || 0)}</span></div>
              <Separator />
              <div className="flex justify-between"><span className="text-muted-foreground">Payment status</span><Badge variant="outline">{booking.paymentStatus}</Badge></div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
